import { DOCUMENT } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { Component, ElementRef, EventEmitter, Input, Output, OnChanges, OnDestroy, Renderer2, SimpleChanges, ViewChild, inject, AfterViewInit, HostListener } from '@angular/core';


@Component({
  selector: 'app-side-drawer',
  templateUrl: './side-drawer.component.html',
  styleUrls: [
    './side-drawer.component.scss',
    './side-drawer.component.layout.scss'
  ],
  standalone: true,
  imports: [CdkTrapFocus]
})
export class SideDrawerComponent implements OnChanges, OnDestroy, AfterViewInit {
  private readonly renderer = inject(Renderer2);
  private readonly documentRef = inject(DOCUMENT);
  private readonly bodyClass = 'app-drawer-open';
  private visualViewport?: VisualViewport;
  private lastFocusedElement: HTMLElement | null = null;
  private readonly onViewportChange = () => {
    if (this.open) {
      this.updateDrawerTop();
    }
  };

  @Input() open = false;
  @Input() width = 600;
  @Input() maxWidth = '95vw';
  @Input() zIndex = 999999;
  @Input() showBackdrop = true;
  @Input() topOffset?: number;
  @Output() closed = new EventEmitter<void>();
  @ViewChild('drawerPanel') drawerPanel?: ElementRef<HTMLElement>;

  drawerTop = 0;
  drawerHeight = 0;
  keyboardTabMode = false;

  close() {
    this.closed.emit();
  }

  ngAfterViewInit() {
    this.bindViewportListeners();
    this.updateDrawerTop();
    if (this.open) {
      this.captureFocusOrigin();
      this.focusDrawerPanel();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const openChange = changes['open'];
    if (openChange) {
      if (openChange.currentValue === true) {
        this.captureFocusOrigin();
        this.keyboardTabMode = false;
      }
      this.toggleBodyClass(this.open);
      if (this.open) {
        this.updateDrawerTop();
        this.focusDrawerPanel();
      } else if (openChange.previousValue === true) {
        this.keyboardTabMode = false;
        this.restoreFocusOrigin();
      }
    }
  }

  ngOnDestroy() {
    this.toggleBodyClass(false);
    this.unbindViewportListeners();
    this.keyboardTabMode = false;
    this.restoreFocusOrigin();
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.open) {
      this.updateDrawerTop();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.open) {
      return;
    }

    if (event.key === 'Tab') {
      this.keyboardTabMode = true;
    }
  }

  @HostListener('document:mousedown')
  onDocumentMousedown(): void {
    this.disableKeyboardTabModeOnPointer();
  }

  @HostListener('document:touchstart')
  onDocumentTouchstart(): void {
    this.disableKeyboardTabModeOnPointer();
  }

  @HostListener('document:pointerdown')
  onDocumentPointerdown(): void {
    this.disableKeyboardTabModeOnPointer();
  }

  private toggleBodyClass(shouldAdd: boolean) {
    const body = this.documentRef.body;
    const html = this.documentRef.documentElement;
    if (!body || !html) return;
    if (shouldAdd) {
      this.renderer.addClass(body, this.bodyClass);
      this.renderer.addClass(html, this.bodyClass);
    } else {
      this.renderer.removeClass(body, this.bodyClass);
      this.renderer.removeClass(html, this.bodyClass);
    }
  }

  private updateDrawerTop() {
    const nextTop = typeof this.topOffset === 'number' ? this.topOffset : this.getResponsiveTopOffset();
    this.drawerTop = Math.max(0, Math.floor(nextTop));
    this.updateDrawerHeight();
    requestAnimationFrame(() => {
      const rafTop = typeof this.topOffset === 'number' ? this.topOffset : this.getResponsiveTopOffset();
      this.drawerTop = Math.max(0, Math.floor(rafTop));
      this.updateDrawerHeight();
    });
  }

  private updateDrawerHeight() {
    const viewportHeight = this.getViewportHeight();
    this.drawerHeight = Math.max(0, Math.floor(viewportHeight - this.drawerTop));
  }

  private getViewportHeight(): number {
    const candidates: number[] = [];
    if (window.innerHeight) {
      candidates.push(window.innerHeight);
    }
    if (document.documentElement?.clientHeight) {
      candidates.push(document.documentElement.clientHeight);
    }
    if (window.visualViewport?.height) {
      candidates.push(window.visualViewport.height);
    }
    if (!candidates.length) {
      return 0;
    }
    return Math.min(...candidates);
  }

  private bindViewportListeners() {
    this.visualViewport = window.visualViewport ?? undefined;
    if (!this.visualViewport) return;
    this.visualViewport.addEventListener('resize', this.onViewportChange, { passive: true });
    this.visualViewport.addEventListener('scroll', this.onViewportChange, { passive: true });
  }

  private unbindViewportListeners() {
    if (!this.visualViewport) return;
    this.visualViewport.removeEventListener('resize', this.onViewportChange);
    this.visualViewport.removeEventListener('scroll', this.onViewportChange);
  }

  private getResponsiveTopOffset(): number {
    return 0;
  }

  private getHeaderTopOffset(): number {
    const subheader = document.querySelector<HTMLElement>('.subheader');
    if (subheader && this.isVisible(subheader)) {
      const style = globalThis.getComputedStyle(subheader);
      const isFixed =
        style.position === 'fixed' ||
        style.position === 'sticky' ||
        document.body.classList.contains('subheader-fixed');
      if (isFixed) {
        const rect = subheader.getBoundingClientRect();
        if (rect.bottom > 0) {
          return rect.bottom;
        }
      }
    }

    const headerHeight = this.getVisibleHeight(['.header', '.header-mobile', '.status-topbar']);
    const bannerHeight = this.getVisibleHeight(['#kt_wrapper > .alert']);
    return headerHeight + bannerHeight;
  }

  private getVisibleHeight(selectors: string[]): number {
    let maxHeight = 0;
    selectors.forEach((selector) => {
      const element = document.querySelector<HTMLElement>(selector);
      if (!element || !this.isVisible(element)) return;
      const rect = element.getBoundingClientRect();
      if (rect.height > maxHeight) {
        maxHeight = rect.height;
      }
    });
    return maxHeight;
  }

  private isVisible(element: HTMLElement): boolean {
    const style = globalThis.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    const rect = element.getBoundingClientRect();
    return rect.height > 0 && rect.width > 0;
  }

  private captureFocusOrigin(): void {
    const activeElement = this.documentRef.activeElement;
    this.lastFocusedElement = activeElement instanceof HTMLElement ? activeElement : null;
  }

  private focusDrawerPanel(): void {
    requestAnimationFrame(() => {
      if (!this.open) {
        return;
      }

      const panel = this.drawerPanel?.nativeElement;
      if (!panel) {
        return;
      }

      const activeElement = this.documentRef.activeElement;
      if (activeElement instanceof HTMLElement && panel.contains(activeElement)) {
        return;
      }

      panel.focus({ preventScroll: true });
    });
  }

  private restoreFocusOrigin(): void {
    const focusTarget = this.lastFocusedElement;
    this.lastFocusedElement = null;

    if (!focusTarget) {
      return;
    }

    requestAnimationFrame(() => {
      try {
        if (this.documentRef.body?.contains(focusTarget)) {
          focusTarget.focus({ preventScroll: true });
        }
      } catch {
        // Ignore focus restoration failures for detached or disabled elements.
      }
    });
  }

  private disableKeyboardTabModeOnPointer(): void {
    if (!this.open) {
      return;
    }

    this.keyboardTabMode = false;
  }
}
